import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import Modal from "react-modal";
import "./TradeSetupTEnEx.css";
import { buildApiUrl, buildScopedApiUrl, ensureArray, getApiHeaders } from "./config";
import { buildTradeServiceUrl } from "./tradeServiceRouting";
import { useWorkspace } from "./workspace/WorkspaceContext";
Modal.setAppElement("#root");

// Profit-oriented percent:
//  LONG  : level > base => +% (profit)
//  SHORT : level < base => +% (profit)
const computePnLPct = (level, base, side) => {
  const lvl  = Number(level);
  const b    = Number(base);
  if (!Number.isFinite(lvl) || !Number.isFinite(b) || b === 0) return null;

  const s = String(side || "").trim().toLowerCase();
  if (s === "open_short") {
    // profit when level < base
    return ((b - lvl) / b) * 100;
  }
  // default LONG
  return ((lvl - b) / b) * 100;
};

const sanitizePct = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.abs(n) : 0;
};

const recalcLevelFromPct = (formData, pct, type) => {
  const basePrice =
    formData.tradeEntryType === "upcoming trade setup" &&
    formData.tradeEntryTrackerBase === "SR_LEVEL" &&
    formData.tradeEntryTrackerValue
      ? Number(formData.tradeEntryTrackerValue)
      : Number(formData.limitPrice);

  if (!Number.isFinite(basePrice) || basePrice <= 0) return "";

  const side = formData.side;
  const factor = pct / 100;

  if (type === "TP") {
    const level =
      side === "open_long" ? basePrice * (1 + factor) : basePrice * (1 - factor);
    return level.toFixed(4);
  } else if (type === "SL") {
    const level =
      side === "open_long" ? basePrice * (1 - factor) : basePrice * (1 + factor);
    return level.toFixed(4);
  }
  return "";
};

const TREND_SHIFT_SIGNAL_OPTIONS = [
  "FIRST_EMA200_BAND_CROSS_AFTER_TREND_SHIFT",
  "FIRST_EMA50_BAND_CROSS_AFTER_TREND_SHIFT",
  "FIRST_TREND_ALIGNED_STOCH_RSI_RESET_AFTER_TREND_SHIFT",
  "FIRST_TREND_ALIGNED_STOCH_RSI_RESET_AT_EMA200_BAND_AFTER_TREND_SHIFT",
  "FIRST_TREND_ALIGNED_STOCH_RSI_RESET_AT_EMA50_BAND_AFTER_TREND_SHIFT",
];

const CROSS_HIDDEN_EMA_OPTIONS = [
  "EMA50_BAND",
  "EMA50_BAND_TREND_ALIGNED",
  "EMA200_BAND",
  "EMA200_BAND_CROSS_TREND_ALIGNED",
  "EMA50_BAND_CHOPPY_RANGE_BREAK_OUT",
  "EMA50_BAND_CHOPPY_RANGE_BREAK_OUT_WITH_VOLUME",
  "EMA50_BAND_CHOPPY_RANGE_BREAK_OUT_TREND_ALIGNED",
];

const MULTI_TIMEFRAME_CONFLUENCE = "MULTI_TIMEFRAME_CONFLUENCE";

const TRADE_SETUP_DEFAULT_CONFIG = {
  entryType: "TRADE_SETUP_DEFAULT_ENTRY_TYPE",
  side: "TRADE_SETUP_DEFAULT_SIDE",
  orderType: "TRADE_SETUP_DEFAULT_ORDER_TYPE",
  tradingTimeFrame: "TRADE_SETUP_DEFAULT_TRADING_TIME_FRAME",
  margin: "TRADE_SETUP_DEFAULT_MARGIN",
  leverage: "TRADE_SETUP_DEFAULT_LEVERAGE",
  tradeType: "TRADE_SETUP_DEFAULT_TRADE_TYPE",
  attemptAllowed: "TRADE_SETUP_DEFAULT_ATTEMPT_ALLOWED",
  entryTrackerBase: "TRADE_SETUP_DEFAULT_ENTRY_TRACKER_BASE",
  entryConfluenceStrategy: "TRADE_SETUP_DEFAULT_ENTRY_CONFLUENCE_STRATEGY",
  entryTrackerValue: "TRADE_SETUP_DEFAULT_ENTRY_TRACKER_VALUE",
  entryTrackerTimeFrame: "TRADE_SETUP_DEFAULT_ENTRY_TRACKER_TIME_FRAME",
  takeProfitTrackerTimeFrame: "TRADE_SETUP_DEFAULT_TAKE_PROFIT_TRACKER_TIME_FRAME",
  ema50XEma200ExitEnabled: "TRADE_SETUP_DEFAULT_EMA50_X_EMA200_EXIT_ENABLED",
  ema50XEma200ExitTimeFrame: "TRADE_SETUP_DEFAULT_EMA50_X_EMA200_EXIT_TIME_FRAME",
  takeProfitPercent: "TRADE_SETUP_DEFAULT_TAKE_PROFIT_PERCENT",
  stopLossPercent: "TRADE_SETUP_DEFAULT_STOP_LOSS_PERCENT",
  addTakeProfitTracker: "TRADE_SETUP_DEFAULT_ADD_TP_TRACKER",
  takeProfitSrEnabled: "TRADE_SETUP_DEFAULT_TP_SR_ENABLED",
  stopLossSrEnabled: "TRADE_SETUP_DEFAULT_SL_SR_ENABLED",
  trailingStopLossEnabled: "TRADE_SETUP_DEFAULT_TRAILING_STOP_LOSS_ENABLED",
  trailingStopLossTimeFrame: "TRADE_SETUP_DEFAULT_TSL_TIME_FRAME",
  trailingStopLossMinProfitPercent: "TRADE_SETUP_DEFAULT_TSL_MIN_PROFIT_PERCENT",
  trailingStopLossCallbackPercent: "TRADE_SETUP_DEFAULT_TSL_CALLBACK_PERCENT",
  trailingStopLossType: "TRADE_SETUP_DEFAULT_TSL_TYPE",
  breakEvenEnabled: "TRADE_SETUP_DEFAULT_BE_ENABLED",
  breakEvenMinProfitPercent: "TRADE_SETUP_DEFAULT_BE_MIN_PROFIT_PERCENT",
  breakEvenTimeFrame: "TRADE_SETUP_DEFAULT_BE_TIME_FRAME",
  partialTakeProfitEnabled: "TRADE_SETUP_DEFAULT_PTP_ENABLED",
  partialTakeProfitMinProfitPercent: "TRADE_SETUP_DEFAULT_PTP_MIN_PROFIT_PERCENT",
  partialTakeProfitCloseSizePercent: "TRADE_SETUP_DEFAULT_PTP_CLOSE_SIZE_PERCENT",
  partialTakeProfitTimeFrame: "TRADE_SETUP_DEFAULT_PTP_TIME_FRAME",
};

const truthyConfig = (value, fallback = false) => {
  if (value === undefined || value === null || value === "") return fallback;
  const normalized = String(value).trim().toLowerCase();
  return ["true", "y", "yes", "1"].includes(normalized);
};

const yesNoConfig = (value, fallback = "NO") => (truthyConfig(value, fallback === "YES") ? "YES" : "NO");

const TRADE_SETUP_EXPIRY_OPTIONS = [
  { value: "NONE", label: "No Expiry" },
  { value: "5H", label: "5 hours" },
  { value: "12H", label: "12 hours" },
  { value: "1D", label: "1 day" },
  { value: "2D", label: "2 days" },
  { value: "1W", label: "1 week" },
  { value: "CUSTOM", label: "Custom" },
];

const createTakeProfitTracker = (overrides = {}) => ({
  enabled: true,
  trackerBase: "EMA",
  tracker: "",
  candleCross: "FIRST_CANDLE_CROSS",
  trackerValue: "",
  specialIdentifier: "",
  volumeBreakoutRequired: false,
  comments: "",
  timeFrame: "15",
  closeSizePercentage: "100",
  ...overrides,
});

const createConfiguredTakeProfitTrackers = ({
  takeProfitPercent = "5",
  stopLossPercent = "-2.5",
  takeProfitTrackerTimeFrame = "15",
  ema50XEma200ExitEnabled = false,
  ema50XEma200ExitTimeFrame = takeProfitTrackerTimeFrame,
} = {}) => [
  createTakeProfitTracker({
    trackerBase: "PROFIT_PERCENT",
    trackerValue: takeProfitPercent,
    specialIdentifier: "TAKE_PROFIT",
    timeFrame: takeProfitTrackerTimeFrame,
  }),
  createTakeProfitTracker({
    trackerBase: "SL_PERCENT",
    trackerValue: stopLossPercent,
    specialIdentifier: "STOP_LOSS",
    timeFrame: takeProfitTrackerTimeFrame,
  }),
  createTakeProfitTracker({
    enabled: ema50XEma200ExitEnabled,
    trackerBase: "EMA50_X_EMA200_CROSS",
    trackerValue: "",
    specialIdentifier: "EMA50_X_EMA200_EXIT",
    timeFrame: ema50XEma200ExitTimeFrame,
    closeSizePercentage: "100",
  }),
];

function TradeSetupTEnEx({ isOpen, closeModal }) {
  const { workspace } = useWorkspace();
  // ------------------------------------------------------
  // Form state
  // ------------------------------------------------------
  const [formData, setFormData] = useState({
    symbol: "",
    side: "open_long",
    orderType: "market", // market/limit/trigger
    tradeEntryType: "upcoming trade setup", // immediate/upcoming trade setup
    limitPrice: "",
    margin: "2",
    leverage: "10",
    tradeType: "FRESH",
    tradingTimeFrame: "15",

    // tracker controls
    addTpTrackerOpt: "YES",
    takeProfitTrackerBase: "PROFIT_PERCENT",
    takeProfitTracker: "",
    tpSlTrackerEmaCandleCross: "FIRST_CANDLE_CROSS",
    tpTrackerValue: "",
    takeProfitTrackers: createConfiguredTakeProfitTrackers(),

    // SR levels (shown editable)
    tpSrLevel: "",
    slSrLevel: "",
    tpSrOpt: "NO",
    slSrOpt: "NO",

    // trade entry tracker fields
    attemptAllowed: 100,
    tradeEntryTrackerBase: MULTI_TIMEFRAME_CONFLUENCE,
    tradeEntryTracker: "",
    tradeEntryTrackerCross: "FIRST_CANDLE_CROSS",
    tradeEntryTrackerType: "",
    tradeEntryTrackerValue: "",
    tradeEntryVolumeBreakoutRequired: false,
    comments: "",
    journaling: "",    
    tpTrackerSpecialIdentifier: "",
    tradeEntryTrackerSpecialIdentifier: "",
    activePositionCountLimitCheck: "REQUIRED",
    tpTrackerComments: "",
    tradeEntryTrackerComments: "",
    tradeEntryTrackerTimeFrame: "15",   // default same as general timeframe
    tradeSetupExpiryMode: "NONE",
    customTradeSetupExpiryValue: "",
    customTradeSetupExpiryUnit: "HOURS",
    takeProfitTrackerTimeFrame: "15",   // default same as general timeframe
    tpSrTimeFrame: "15",                // UI-only for Take Profit SR
    slSrTimeFrame: "15",                // UI-only for Stop Loss SR
    trailingStopLossRequired: true,
    trailingStopLossType: "PREV_CANDLE_TREND_REVERSAL_SL",
    breakEvenRequired: true,
    partialTakeProfitRequired: true,
    dcaRequired: false,

    // Default Min Profit %
    tslMinProfitPercent: "6",
    tslCallbackPercent: "1.5",
    beMinProfitPercent: "4",
    ptpMinProfitPercent: "3",
    dcaMinProfitPercent: "2",

    // Default SR TimeFrames
    trailingStopLossSRTimeFrame: 15,
    breakEvenSRTimeFrame: 15,
    partialTakeProfitSRTimeFrame: 15,
    dcaTimeFrame: 15,

    // Default Size %
    ptpCloseSizePercent: "50",
    dcaAddSizePercent: "50",
    exchange: workspace.exchange,
    marketType: workspace.marketType,

  });

  const [tpCustomPct, setTpCustomPct] = useState(5); // default 5%
  const [slCustomPct, setSlCustomPct] = useState(2); // default 2%
  const [targetPercentageTP, setTargetPercentageTP] = useState('');
  const [targetPercentageSL, setTargetPercentageSL] = useState('');

  const EMA_OPTIONS_LONG = [
    "EMA50_BAND",
    "EMA50_BAND_TREND_ALIGNED",
    "EMA200_BAND",
    "EMA200_BAND_CROSS_TREND_ALIGNED",
    "EMA50_BAND_CHOPPY_RANGE_BREAK_OUT",
    "EMA50_BAND_CHOPPY_RANGE_BREAK_OUT_WITH_VOLUME",
    "EMA50_BAND_CHOPPY_RANGE_BREAK_OUT_TREND_ALIGNED",
    "EMA12", "EMA21", "EMA36", "EMA50", "EMA100", "EMA200",
    "ALL_EMA_STACK_TOP_EMA",
    "LOWER_LENGTH_EMA_STACK_TOP_EMA"
  ];

  const EMA_OPTIONS_SHORT = [
    "EMA50_BAND",
    "EMA50_BAND_TREND_ALIGNED",
    "EMA200_BAND",
    "EMA200_BAND_CROSS_TREND_ALIGNED",
    "EMA50_BAND_CHOPPY_RANGE_BREAK_OUT",
    "EMA50_BAND_CHOPPY_RANGE_BREAK_OUT_WITH_VOLUME",
    "EMA50_BAND_CHOPPY_RANGE_BREAK_OUT_TREND_ALIGNED",
    "EMA12", "EMA21", "EMA36", "EMA50", "EMA100", "EMA200",
    "ALL_EMA_STACK_BOTTOM_EMA",
    "LOWER_LENGTH_EMA_STACK_BOTTOM_EMA"
  ];

  const getFilteredEmaOptions = (side) => {
      if (side === "open_long") return EMA_OPTIONS_LONG;
      if (side === "open_short") return EMA_OPTIONS_SHORT;
      return []; // nothing if side not selected
    };

  const TIMEFRAME_OPTIONS = useMemo(
    () => [5, 15, 30, 60, 240, 480, 720, 1440],
    []
  );

const TP_BASE_OPTIONS = [
  "EMA",
  "EMA50_X_EMA200_CROSS",
  "TREND_SHIFT_SIGNAL",
  "CANDLE_TREND_REVERSAL",
  "PREV_OPP_CANDLE_CLOSE_SL",
  "PREV_CANDLE_TREND_REVERSAL_SL",
  "PREV_LES_EDGE_CROSS_SL",
  "PROFIT_PERCENT",
  "SL_PERCENT",
  "SWING_LEVEL_STOCH_RSI_REVERSAL",
  "RSI_DIVERGENCE",
  "AO_DIVERGENCE",
  "FVG_TAGGED",
  "CANDLE_COLOR",
  "VWAP",
  "RSI_AND_SRSI_REVERSAL",
  "SWING_HIGH_LOW",

];

const TE_BASE_OPTIONS = [
  "SR_LEVEL",
  "EMA",
    "MULTI_TIMEFRAME_CONFLUENCE",
  "TREND_SHIFT_SIGNAL",
  "TREND_ALIGNED_STOCH_RSI_REVERSAL",
  "SWING_LEVEL_STOCH_RSI_REVERSAL",
  "RSI_DIVERGENCE",
  "AO_DIVERGENCE",
  "FVG_TAGGED",
  "CANDLE_TREND_REVERSAL",
  "VWAP",  
  "CANDLE_COLOR",
  "RSI_AND_SRSI_REVERSAL",
  "SWING_HIGH_LOW",  
];

  const CROSS_HIDDEN_BASES = [
    "CANDLE_COLOR",
    "RSI_AND_SRSI_REVERSAL",
    "PROFIT_PERCENT",
    "SL_PERCENT",    
    "EMA50_X_EMA200_CROSS",
    "CANDLE_TREND_REVERSAL",
    "PREV_OPP_CANDLE_CLOSE_SL",
    "PREV_CANDLE_TREND_REVERSAL_SL",
    "PREV_LES_EDGE_CROSS_SL",
    "CANDLE_TREND_REVERSAL",    
    "TREND_ALIGNED_STOCH_RSI_REVERSAL",
    "SWING_LEVEL_STOCH_RSI_REVERSAL",
    "RSI_DIVERGENCE",
    "AO_DIVERGENCE",
    "FVG_TAGGED",
    "MULTI_TIMEFRAME_CONFLUENCE",
    "TREND_SHIFT_SIGNAL",
    ];

const buildTradeEntryTrackerType = (base, value, crossMode, side) => {
  if (!base) return "";

  // Ã°Å¸â€Â¹ Auto-resolve candle reversal based on side
  if (base === "CANDLE_TREND_REVERSAL") {
    base = side === "open_long"
      ? "BULLISH_CANDLE_TREND_REVERSAL"
      : side === "open_short"
      ? "BEARISH_CANDLE_TREND_REVERSAL"
      : base;
  }

  if (base === MULTI_TIMEFRAME_CONFLUENCE) {
    return MULTI_TIMEFRAME_CONFLUENCE;
  }

  const simpleBases = [
    "CANDLE_COLOR",
    "RSI_AND_SRSI_REVERSAL",
    "PROFIT_PERCENT",
    "SR_LEVEL",
    "BULLISH_CANDLE_TREND_REVERSAL",
    "BEARISH_CANDLE_TREND_REVERSAL",
    "SL_PERCENT",    
    "PREV_OPP_CANDLE_CLOSE_SL",
    "PREV_CANDLE_TREND_REVERSAL_SL",
    "PREV_LES_EDGE_CROSS_SL",    
    "TREND_ALIGNED_STOCH_RSI_REVERSAL",
    "SWING_LEVEL_STOCH_RSI_REVERSAL",
    "RSI_DIVERGENCE",
    "AO_DIVERGENCE",
    "FVG_TAGGED",

  ];

  if (simpleBases.includes(base) ) {
    return base;
  }

  if (base === "TREND_SHIFT_SIGNAL") {
    return value || "";
  }

  if (CROSS_HIDDEN_EMA_OPTIONS.includes(value)) {
    return value;
  }

  if (!value || !crossMode || !side) return "";
  const dir = side === "open_long" ? "BULL" : side === "open_short" ? "BEAR" : "";
  const crossPrefix =
    crossMode === "FIRST_CANDLE_CROSS" ? "FIRST_CANDLE" : "CONFIRMED";
  return `${value}_${crossPrefix}_${dir}_CROSS`;
};

const composedTradeEntryTrackerType = useMemo(
  () =>
    buildTradeEntryTrackerType(
      formData.tradeEntryTrackerBase,
      formData.tradeEntryTracker,
      formData.tradeEntryTrackerCross,
      formData.side
    ),
  [
    formData.tradeEntryTrackerBase,
    formData.tradeEntryTracker,
    formData.tradeEntryTrackerCross,
    formData.side,
  ]
);

const buildTakeProfitTrackerType = (base, value, crossMode, side) => {
  if (!base) return "";

  // Handle candle reversal types
  if (base === "CANDLE_TREND_REVERSAL") {
    return side === "open_long"
      ? "BEARISH_CANDLE_TREND_REVERSAL"
      : side === "open_short"
      ? "BULLISH_CANDLE_TREND_REVERSAL"
      : "CANDLE_TREND_REVERSAL";
  }

  if (base === MULTI_TIMEFRAME_CONFLUENCE) {
    return MULTI_TIMEFRAME_CONFLUENCE;
  }

  const simpleBases = [
    "CANDLE_COLOR",
    "RSI_AND_SRSI_REVERSAL",
    "PROFIT_PERCENT",
    "SL_PERCENT", 
    "SR_LEVEL",
    "BULLISH_CANDLE_TREND_REVERSAL",
    "BEARISH_CANDLE_TREND_REVERSAL",
    "PREV_OPP_CANDLE_CLOSE_SL",
    "PREV_CANDLE_TREND_REVERSAL_SL",
    "PREV_LES_EDGE_CROSS_SL",
    "TREND_ALIGNED_STOCH_RSI_REVERSAL",
    "SWING_LEVEL_STOCH_RSI_REVERSAL",
    "RSI_DIVERGENCE",
    "AO_DIVERGENCE",
    "FVG_TAGGED",
    "EMA50_X_EMA200_CROSS",
    ];

  if (simpleBases.includes(base)) return base;
  if (base === "TREND_SHIFT_SIGNAL") return value || "";
  if (CROSS_HIDDEN_EMA_OPTIONS.includes(value)) return value;

  if (!value || !crossMode || !side) return "";

  const dir = side === "open_long" ? "BEAR" : side === "open_short" ? "BULL" : "";
  const crossPrefix =
    crossMode === "FIRST_CANDLE_CROSS" ? "FIRST_CANDLE" : "CONFIRMED";

  return `${value}_${crossPrefix}_${dir}_CROSS`;
};

  const [symbolOptions, setSymbolOptions] = useState([]);
  const [bulkSymbolsText, setBulkSymbolsText] = useState("");
  const [submissionResult, setSubmissionResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confluenceStrategies, setConfluenceStrategies] = useState([]);
  const [tradeSetupDefaults, setTradeSetupDefaults] = useState(null);
  const defaultsAppliedRef = useRef(false);

  const selectedConfluenceStrategy = useMemo(
    () =>
      confluenceStrategies.find(
        (item) => item?.strategy?.strategyCode === formData.tradeEntryTrackerValue
      ),
    [confluenceStrategies, formData.tradeEntryTrackerValue]
  );
  const selectedConfluenceFinalTimeFrame = selectedConfluenceStrategy?.strategy?.finalTriggerTimeFrame
    ? String(selectedConfluenceStrategy.strategy.finalTriggerTimeFrame)
    : "";

  const buildTradeSetupDefaults = useCallback((configRows = [], previousForm = {}) => {
    const configMap = new Map(
      ensureArray(configRows)
        .filter((cfg) => cfg?.configKey)
        .map((cfg) => [cfg.configKey, cfg.configValue])
    );

    const readConfig = (key, fallback) => {
      const value = configMap.get(key);
      if (value === undefined || value === null || String(value).trim() === "") {
        return fallback;
      }
      return String(value).trim();
    };

    const entryBase = readConfig(TRADE_SETUP_DEFAULT_CONFIG.entryTrackerBase, MULTI_TIMEFRAME_CONFLUENCE);
    const configuredStrategyCode = readConfig(TRADE_SETUP_DEFAULT_CONFIG.entryConfluenceStrategy, "");
    const fallbackStrategyCode = confluenceStrategies[0]?.strategy?.strategyCode || "";
    const strategyCode = entryBase === MULTI_TIMEFRAME_CONFLUENCE
      ? configuredStrategyCode || fallbackStrategyCode
      : "";
    const strategy = confluenceStrategies.find((item) => item?.strategy?.strategyCode === strategyCode)?.strategy;
    const configuredTradingTimeFrame = readConfig(TRADE_SETUP_DEFAULT_CONFIG.tradingTimeFrame, previousForm.tradingTimeFrame || "15");
    const finalTriggerTimeFrame = strategy?.finalTriggerTimeFrame ? String(strategy.finalTriggerTimeFrame) : "";
    const tradingTimeFrame = finalTriggerTimeFrame || configuredTradingTimeFrame;
    const entryTrackerTimeFrame = finalTriggerTimeFrame || readConfig(
      TRADE_SETUP_DEFAULT_CONFIG.entryTrackerTimeFrame,
      previousForm.tradeEntryTrackerTimeFrame || tradingTimeFrame
    );
    const takeProfitTrackerTimeFrame = readConfig(
      TRADE_SETUP_DEFAULT_CONFIG.takeProfitTrackerTimeFrame,
      previousForm.takeProfitTrackerTimeFrame || "15"
    );
    const takeProfitPercent = readConfig(TRADE_SETUP_DEFAULT_CONFIG.takeProfitPercent, "5");
    const stopLossPercent = readConfig(TRADE_SETUP_DEFAULT_CONFIG.stopLossPercent, "-2.5");
    const ema50XEma200ExitTimeFrame = readConfig(
      TRADE_SETUP_DEFAULT_CONFIG.ema50XEma200ExitTimeFrame,
      takeProfitTrackerTimeFrame
    );
    const ema50XEma200ExitEnabled = truthyConfig(
      readConfig(TRADE_SETUP_DEFAULT_CONFIG.ema50XEma200ExitEnabled, "NO"),
      false
    );
    const configuredTrailingStopLossType = readConfig(
      TRADE_SETUP_DEFAULT_CONFIG.trailingStopLossType,
      "PREV_CANDLE_TREND_REVERSAL_SL"
    );
    const trailingStopLossType = configuredTrailingStopLossType.toUpperCase() === "PERCENT_CALLBACK_SL"
      ? "PERCENT_CALLBACK_TSL"
      : configuredTrailingStopLossType.toUpperCase();

    return {
      side: readConfig(TRADE_SETUP_DEFAULT_CONFIG.side, "open_long"),
      orderType: readConfig(TRADE_SETUP_DEFAULT_CONFIG.orderType, "market"),
      tradeEntryType: readConfig(TRADE_SETUP_DEFAULT_CONFIG.entryType, "upcoming trade setup"),
      margin: readConfig(TRADE_SETUP_DEFAULT_CONFIG.margin, "2"),
      leverage: readConfig(TRADE_SETUP_DEFAULT_CONFIG.leverage, "10"),
      tradeType: readConfig(TRADE_SETUP_DEFAULT_CONFIG.tradeType, "FRESH"),
      attemptAllowed: Number(readConfig(TRADE_SETUP_DEFAULT_CONFIG.attemptAllowed, "100")) || 100,
      tradingTimeFrame,
      tradeEntryTrackerTimeFrame: entryTrackerTimeFrame,
      tradeEntryTrackerBase: entryBase,
      tradeEntryTracker: entryBase === MULTI_TIMEFRAME_CONFLUENCE
        ? ""
        : readConfig(TRADE_SETUP_DEFAULT_CONFIG.entryTrackerValue, previousForm.tradeEntryTracker || ""),
      tradeEntryTrackerValue: entryBase === MULTI_TIMEFRAME_CONFLUENCE
        ? strategyCode
        : readConfig(TRADE_SETUP_DEFAULT_CONFIG.entryTrackerValue, previousForm.tradeEntryTrackerValue || ""),
      tradeEntryTrackerCross: "FIRST_CANDLE_CROSS",
      addTpTrackerOpt: yesNoConfig(readConfig(TRADE_SETUP_DEFAULT_CONFIG.addTakeProfitTracker, "YES"), "YES"),
      tpSrOpt: yesNoConfig(readConfig(TRADE_SETUP_DEFAULT_CONFIG.takeProfitSrEnabled, "NO"), "NO"),
      slSrOpt: yesNoConfig(readConfig(TRADE_SETUP_DEFAULT_CONFIG.stopLossSrEnabled, "NO"), "NO"),
      takeProfitTrackerTimeFrame,
      tpSrTimeFrame: takeProfitTrackerTimeFrame,
      slSrTimeFrame: takeProfitTrackerTimeFrame,
      trailingStopLossRequired: truthyConfig(
        readConfig(TRADE_SETUP_DEFAULT_CONFIG.trailingStopLossEnabled, "true"),
        true
      ),
      trailingStopLossSRTimeFrame: readConfig(TRADE_SETUP_DEFAULT_CONFIG.trailingStopLossTimeFrame, "15"),
      tslMinProfitPercent: readConfig(TRADE_SETUP_DEFAULT_CONFIG.trailingStopLossMinProfitPercent, "6"),
      tslCallbackPercent: readConfig(TRADE_SETUP_DEFAULT_CONFIG.trailingStopLossCallbackPercent, "1.5"),
      trailingStopLossType,
      breakEvenRequired: truthyConfig(
        readConfig(TRADE_SETUP_DEFAULT_CONFIG.breakEvenEnabled, "true"),
        true
      ),
      beMinProfitPercent: readConfig(TRADE_SETUP_DEFAULT_CONFIG.breakEvenMinProfitPercent, "4"),
      breakEvenSRTimeFrame: readConfig(TRADE_SETUP_DEFAULT_CONFIG.breakEvenTimeFrame, "15"),
      partialTakeProfitRequired: truthyConfig(
        readConfig(TRADE_SETUP_DEFAULT_CONFIG.partialTakeProfitEnabled, "true"),
        true
      ),
      ptpMinProfitPercent: readConfig(TRADE_SETUP_DEFAULT_CONFIG.partialTakeProfitMinProfitPercent, "3"),
      ptpCloseSizePercent: readConfig(TRADE_SETUP_DEFAULT_CONFIG.partialTakeProfitCloseSizePercent, "50"),
      partialTakeProfitSRTimeFrame: readConfig(TRADE_SETUP_DEFAULT_CONFIG.partialTakeProfitTimeFrame, "15"),
      takeProfitTrackers: createConfiguredTakeProfitTrackers({
        takeProfitPercent,
        stopLossPercent,
        takeProfitTrackerTimeFrame,
        ema50XEma200ExitEnabled,
        ema50XEma200ExitTimeFrame,
      }),
    };
  }, [confluenceStrategies]);

  useEffect(() => {
    if (formData.tradeEntryTrackerBase !== MULTI_TIMEFRAME_CONFLUENCE || !selectedConfluenceFinalTimeFrame) {
      return;
    }

    setFormData((prev) => {
      if (
        prev.tradingTimeFrame === selectedConfluenceFinalTimeFrame &&
        prev.tradeEntryTrackerTimeFrame === selectedConfluenceFinalTimeFrame
      ) {
        return prev;
      }
      return {
        ...prev,
        tradingTimeFrame: selectedConfluenceFinalTimeFrame,
        tradeEntryTrackerTimeFrame: selectedConfluenceFinalTimeFrame,
      };
    });
  }, [formData.tradeEntryTrackerBase, selectedConfluenceFinalTimeFrame]);

  const resolveTpTrackerSpecialIdentifier = useCallback((tracker) => {
    if (tracker.trackerBase === "SL_PERCENT") {
      return "STOP_LOSS";
    }
    if (tracker.trackerBase === "PROFIT_PERCENT") {
      return "TAKE_PROFIT";
    }
    return tracker.specialIdentifier || "";
  }, []);

  const composeTakeProfitTrackerType = useCallback(
    (tracker) =>
      buildTakeProfitTrackerType(
        tracker.trackerBase,
        tracker.tracker,
        tracker.candleCross,
        formData.side
      ),
    [formData.side]
  );

  const updateTakeProfitTracker = useCallback((index, changes) => {
    setFormData((prev) => ({
      ...prev,
      takeProfitTrackers: prev.takeProfitTrackers.map((tracker, i) =>
        i === index ? { ...tracker, ...changes } : tracker
      ),
    }));
  }, []);

  const getPrimaryTakeProfitTrackerTimeFrame = useCallback(
    () =>
      formData.takeProfitTrackers?.[0]?.timeFrame ||
      formData.takeProfitTrackerTimeFrame ||
      formData.tradingTimeFrame,
    [formData.takeProfitTrackerTimeFrame, formData.takeProfitTrackers, formData.tradingTimeFrame]
  );

  const getPrimaryTakeProfitTrackerComments = useCallback(
    () => formData.takeProfitTrackers?.[0]?.comments || formData.tpTrackerComments,
    [formData.takeProfitTrackers, formData.tpTrackerComments]
  );

  const buildTakeProfitTrackerPayloads = useCallback(
    (tradeSetupRequestId, tpTrackerSourceValue, symbol = formData.symbol) => {
      if (formData.addTpTrackerOpt !== "YES") return [];

      return formData.takeProfitTrackers
        .filter((tracker, index) => index < 2 || tracker.enabled)
        .map((tracker) => ({
          tracker,
          takeProfitType: composeTakeProfitTrackerType(tracker),
        }))
        .filter(({ tracker, takeProfitType }) => tracker.trackerBase && takeProfitType)
        .map(({ tracker, takeProfitType }) => ({
          tradeSetupRequestId,
          symbol,
          exchange: workspace.exchange,
          marketType: workspace.marketType,
          tradeSide: formData.side,
          timeFrame: String(tracker.timeFrame || formData.tradingTimeFrame),
          takeProfitType,
          takeProfitTrackerStartedOn: new Date().toISOString(),
          expectedCandleColorToClose: "",
          activeFlag: "Y",
          tpTrackerValue: tracker.trackerValue,
          tpTrackerSource: tpTrackerSourceValue,
          trackerSpecialIdentifier:
            resolveTpTrackerSpecialIdentifier(tracker) || "BASE_TP",
          closeSizePercentage: tracker.closeSizePercentage || "100",
          volumeBreakoutRequired: Boolean(tracker.volumeBreakoutRequired),
          comments: tracker.comments,
        }));
    },
    [
      composeTakeProfitTrackerType,
      formData.addTpTrackerOpt,
      formData.side,
      formData.symbol,
      formData.takeProfitTrackers,
      formData.tradingTimeFrame,
      resolveTpTrackerSpecialIdentifier,
      workspace.exchange,
      workspace.marketType,
    ]
  );

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      exchange: workspace.exchange,
      marketType: workspace.marketType,
    }));
  }, [workspace]);

  // ------------------------------------------------------
  // Derived helpers
  // ------------------------------------------------------
    const defaultLevelsFor = useCallback((side, limitPrice) => {
      const p = Number.parseFloat(limitPrice);
      if (!Number.isFinite(p) || p <= 0) return {};

      const tpPct = 0.05; // 5% for Take Profit
      const slPct = 0.02; // 2% for Stop Loss

      const upTP = p * (1 + tpPct);
      const dnSL = p * (1 - slPct);

      if (side === "open_long") return { tp: upTP.toFixed(4), sl: dnSL.toFixed(4) };
      if (side === "open_short") return { tp: dnSL.toFixed(4), sl: upTP.toFixed(4) };
      return {};
    }, []);

// ------------------------------------------------------
// Dynamic % calculations for TP/SL based on entry type
// ------------------------------------------------------
const tpChangePct = useMemo(() => {
  // determine base price contextually
  const basePrice =
    formData.tradeEntryType === "upcoming trade setup" &&
    formData.tradeEntryTrackerBase === "SR_LEVEL" &&
    formData.tradeEntryTrackerValue
      ? formData.tradeEntryTrackerValue
      : formData.limitPrice;

  return computePnLPct(formData.tpSrLevel, basePrice, formData.side);
}, [
  formData.tpSrLevel,
  formData.limitPrice,
  formData.side,
  formData.tradeEntryType,
  formData.tradeEntryTrackerBase,
  formData.tradeEntryTrackerValue,
]);

const slChangePct = useMemo(() => {
  const basePrice =
    formData.tradeEntryType === "upcoming trade setup" &&
    formData.tradeEntryTrackerBase === "SR_LEVEL" &&
    formData.tradeEntryTrackerValue
      ? formData.tradeEntryTrackerValue
      : formData.limitPrice;

  return computePnLPct(formData.slSrLevel, basePrice, formData.side);
}, [
  formData.slSrLevel,
  formData.limitPrice,
  formData.side,
  formData.tradeEntryType,
  formData.tradeEntryTrackerBase,
  formData.tradeEntryTrackerValue,
]);

useEffect(() => {
  const basePrice =
    formData.tradeEntryTrackerBase === "SR_LEVEL" &&
    formData.tradeEntryTrackerValue
      ? Number(formData.tradeEntryTrackerValue)
      : Number(formData.limitPrice);

  if (!Number.isFinite(basePrice) || basePrice <= 0) return;

  const tpPct = computePnLPct(formData.tpSrLevel, basePrice, formData.side);
  const slPct = computePnLPct(formData.slSrLevel, basePrice, formData.side);

  // For SL, always show negative side (loss)
  const adjustedSlPct =
    slPct && Number(slPct) > 0 ? -Math.abs(slPct) : slPct;

  setTargetPercentageTP(tpPct ? tpPct.toFixed(2) : "");
  setTargetPercentageSL(adjustedSlPct ? adjustedSlPct.toFixed(2) : "");
}, [
  formData.tpSrLevel,
  formData.slSrLevel,
  formData.limitPrice,
  formData.tradeEntryTrackerValue,
  formData.side,
  formData.tradeEntryTrackerBase,
]);

  // ------------------------------------------------------
  // Effects
  // ------------------------------------------------------
  useEffect(() => {
    if (!isOpen) {
      defaultsAppliedRef.current = false;
      return;
    }
    if (defaultsAppliedRef.current) {
      return;
    }

    defaultsAppliedRef.current = true;
    fetch(buildApiUrl("/api/bot/ui/post/getSignalFlyConfiguration"), {
      headers: getApiHeaders(),
    })
      .then((r) => r.json())
      .then((list) => {
        const defaults = buildTradeSetupDefaults(list, formData);
        setTradeSetupDefaults(defaults);
        setFormData((prev) => ({
          ...prev,
          ...defaults,
        }));
      })
      .catch((e) => {
        console.warn("Failed to load Trade Setup defaults from SignalFly configuration", e);
      });
  // Defaults are intentionally applied once per modal open; user edits should not be overwritten.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, buildTradeSetupDefaults]);

  // load symbols
  useEffect(() => {
    fetch(
      buildScopedApiUrl("/api/bot/ui/get/getRecentActiveAlertSymbols", workspace),
      {
        headers: getApiHeaders()
      })
      .then((r) => r.json())
      .then((list) => {
        const safeList = ensureArray(list);
        if (!Array.isArray(list)) {
          console.warn("Unexpected trade setup symbol payload shape", workspace, list);
        }
        const uniq = Array.from(new Set(safeList)).filter(Boolean);
        setSymbolOptions(uniq);
        setFormData((p) => ({
          ...p,
          symbol: uniq.includes(p.symbol) ? p.symbol : (uniq[0] || ""),
          tradingTimeFrame: p.tradingTimeFrame || "15"
        }));
      });
  }, [workspace]);

  useEffect(() => {
    fetch(buildScopedApiUrl("/api/bot/ui/get/confluenceStrategies", workspace), {
      headers: getApiHeaders(),
    })
      .then((r) => r.json())
      .then((list) => {
        const safeList = ensureArray(list);
        setConfluenceStrategies(safeList);
        setFormData((prev) => {
          if (prev.tradeEntryTrackerBase !== MULTI_TIMEFRAME_CONFLUENCE || prev.tradeEntryTrackerValue) {
            return prev;
          }
          return {
            ...prev,
            tradeEntryTrackerValue: safeList[0]?.strategy?.strategyCode || "",
          };
        });
      })
      .catch((e) => console.warn("Failed to load confluence strategies", e));
  }, [workspace]);

  // get mark price + auto SR Ã‚Â±2%
  useEffect(() => {
    const fetchMark = async () => {
      if (!formData.symbol) return;
      const res = await fetch(
        buildTradeServiceUrl("/api/bot/get/getMarkPriceBySymbol", workspace, {
          symbol: formData.symbol,
        }),
        {
          headers: getApiHeaders()
        });
      const data = await res.json();
      const baseRefPrice =
      formData.tradeEntryType === "upcoming trade setup" &&
      formData.tradeEntryTrackerBase === "SR_LEVEL" &&
      formData.tradeEntryTrackerValue
        ? Number(formData.tradeEntryTrackerValue)
        : data.markPrice;

    const { tp, sl } = defaultLevelsFor(formData.side, baseRefPrice);

      setFormData((p) => ({
        ...p,
        limitPrice: data.markPrice,
        tpSrLevel: tp || p.tpSrLevel,
        slSrLevel: sl || p.slSrLevel,
        tradeEntryTrackerValue:
        p.tradeEntryTrackerBase === "SR_LEVEL"
          ? data.markPrice
          : p.tradeEntryTrackerValue,
      }));
      setFormData(p => ({
        ...p,
        tradeEntryTrackerTimeFrame: p.tradeEntryTrackerTimeFrame || p.tradingTimeFrame,
        takeProfitTrackerTimeFrame: p.takeProfitTrackerTimeFrame || p.tradingTimeFrame,
      }));
    };
    fetchMark();
  }, [formData.symbol, formData.side, defaultLevelsFor, workspace]);

  useEffect(() => {
  setFormData((prev) => ({
    ...prev,
    tradeEntryTrackerTimeFrame: formData.tradingTimeFrame,
    takeProfitTrackerTimeFrame: formData.takeProfitTrackerTimeFrame || formData.tradingTimeFrame,
      }));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.tradingTimeFrame]);

// Ã¢Å“â€¦ FIXED: Auto-updates TP/SL for both immediate & upcoming types
useEffect(() => {
  const entrySR =
    formData.tradeEntryTrackerBase === "SR_LEVEL" &&
    formData.tradeEntryTrackerValue
      ? Number(formData.tradeEntryTrackerValue)
      : Number(formData.limitPrice);

  const side = formData.side;
  if (!Number.isFinite(entrySR) || !side) return;

  let tp, sl;
  if (side === "open_long") {
    tp = entrySR * 1.05; // +5%
    sl = entrySR * 0.98; // -2%
  } else if (side === "open_short") {
    tp = entrySR * 0.95; // -5%
    sl = entrySR * 1.02; // +2%
  }

  setFormData(prev => ({
    ...prev,
    tpSrLevel: tp.toFixed(4),
    slSrLevel: sl.toFixed(4),
  }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [formData.limitPrice, formData.tradeEntryTrackerValue, formData.side, formData.tradeEntryType]);

  // ------------------------------------------------------
  // Field handlers
  // ------------------------------------------------------
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "tpSrOpt" && value === "NO") {
      setFormData((p) => ({ ...p, tpSrOpt: value, tpSrLevel: "" }));
      return;
    }
    if (name === "slSrOpt" && value === "NO") {
      setFormData((p) => ({ ...p, slSrOpt: value, slSrLevel: "" }));
      return;
    }

    if (name === "addTpTrackerOpt" && value === "NO") {
      setFormData((p) => ({
        ...p,
        addTpTrackerOpt: "NO",
        takeProfitTrackerBase: "",
        takeProfitTracker: "",
        tpSlTrackerEmaCandleCross: "",
        tpTrackerValue: "",
        tradeSetupExpiryMode: "NONE",
        customTradeSetupExpiryValue: "",
        customTradeSetupExpiryUnit: "HOURS",
      }));
      return;
    }
    if (name === "addTpTrackerOpt" && value === "YES") {
      setFormData((p) => ({
        ...p,
        addTpTrackerOpt: "YES",
      }));
      return;
    }

    if (name === "tradeEntryType" && value === "immediate") {
      setFormData((p) => ({
        ...p,
        tradeEntryType: "immediate",
        tradeType: "FRESH",
        attemptAllowed: 1,
        tradingTimeFrame: "15",
        tradeEntryTrackerTimeFrame: "15",
        tradeEntryTrackerBase: "",
        tradeEntryTracker: "FIRST_TREND_ALIGNED_STOCH_RSI_RESET_AT_EMA200_BAND_AFTER_TREND_SHIFT",
        tradeEntryTrackerCross: "FIRST_CANDLE_CROSS",
        tradeEntryTrackerValue: "",
        tradeEntryVolumeBreakoutRequired: false,
        tpSrOpt: "YES",
        slSrOpt: "YES",
        addTpTrackerOpt: "NO",
        takeProfitTrackerBase: "",
        takeProfitTracker: "",
        tpSlTrackerEmaCandleCross: "",
        tpTrackerValue: "",
      }));
      return;
    }

    if (name === "tradeEntryType" && value === "upcoming trade setup") {
      setFormData((p) => {
        const defaults = tradeSetupDefaults || buildTradeSetupDefaults([], p);
        return {
          ...p,
          ...defaults,
          tradeEntryType: "upcoming trade setup",
          tradeSetupExpiryMode: p.tradeSetupExpiryMode || "NONE",
          customTradeSetupExpiryValue: p.customTradeSetupExpiryValue || "",
          customTradeSetupExpiryUnit: p.customTradeSetupExpiryUnit || "HOURS",
          tradeEntryVolumeBreakoutRequired: false,
          tpSrLevel: "",
          slSrLevel: "",
        };
      });
      return;
    }

    if (name === "tradeEntryTrackerBase" && value === MULTI_TIMEFRAME_CONFLUENCE) {
      const defaultStrategy = confluenceStrategies[0]?.strategy;
      setFormData((p) => {
        const finalTimeFrame = String(defaultStrategy?.finalTriggerTimeFrame || p.tradingTimeFrame || "15");
        return {
          ...p,
          tradeEntryTrackerBase: MULTI_TIMEFRAME_CONFLUENCE,
          tradeEntryTracker: "",
          tradeEntryTrackerCross: "FIRST_CANDLE_CROSS",
          tradeEntryTrackerValue: defaultStrategy?.strategyCode || "",
          tradingTimeFrame: finalTimeFrame,
          tradeEntryTrackerTimeFrame: finalTimeFrame,
        };
      });
      return;
    }

    setFormData((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  const calculateTradeSetupExpiresAt = () => {
    if (formData.tradeEntryType !== "upcoming trade setup") return null;

    const mode = formData.tradeSetupExpiryMode || "NONE";
    const durationMap = {
      "5H": { value: 5, unit: "HOURS" },
      "12H": { value: 12, unit: "HOURS" },
      "1D": { value: 1, unit: "DAYS" },
      "2D": { value: 2, unit: "DAYS" },
      "1W": { value: 1, unit: "WEEKS" },
    };

    const selected =
      mode === "CUSTOM"
        ? {
            value: Number(formData.customTradeSetupExpiryValue),
            unit: formData.customTradeSetupExpiryUnit || "HOURS",
          }
        : durationMap[mode];

    if (!selected || !Number.isFinite(selected.value) || selected.value <= 0) {
      return null;
    }

    const unitMultipliers = {
      HOURS: 60 * 60 * 1000,
      DAYS: 24 * 60 * 60 * 1000,
      WEEKS: 7 * 24 * 60 * 60 * 1000,
    };
    const multiplier = unitMultipliers[selected.unit] || unitMultipliers.HOURS;
    return new Date(Date.now() + selected.value * multiplier).toISOString();
  };

  // ------------------------------------------------------
  // Submit handler
  // ------------------------------------------------------
  const submitBulkUpcomingTradeSetupForSymbol = async (symbol) => {
    const tradeSetupExpiresAt = calculateTradeSetupExpiresAt();
    const entryPayload = {
      symbol,
      exchange: workspace.exchange,
      marketType: workspace.marketType,
      timeFrame: Number(formData.tradeEntryTrackerTimeFrame || formData.tradingTimeFrame),
      tradeSide: formData.side,
      margin: formData.margin,
      leverage: formData.leverage,
      tradeType: formData.tradeType,
      orderType: formData.orderType,
      tradeEntryType: formData.tradeEntryType,
      tradeEntryTrackerStartedOn: new Date().toISOString(),
      tradeEntryTrackerType: composedTradeEntryTrackerType,
      tradeEntryTrackerValue: formData.tradeEntryTrackerValue,
      expectedCandleColorToOpen: "",
      attemptAllowed: Number(formData.attemptAllowed) || 1,
      attemptCompleted: 0,
      positionOpened: false,
      activeFlag: "Y",
      comments: formData.tradeEntryTrackerComments,
      journaling: formData.journaling,
      confluences: formData.confluences
        ? formData.confluences.split(",").map((value) => value.trim())
        : [],
      trackerSpecialIdentifier: formData.tradeEntryTrackerSpecialIdentifier || "TRADE_ENTRY",
      volumeBreakoutRequired: Boolean(formData.tradeEntryVolumeBreakoutRequired),
      expiryEnabled: Boolean(tradeSetupExpiresAt),
      expiresAt: tradeSetupExpiresAt,
      createdBy: "SYSTEM",
      trailingStopLossRequired: formData.trailingStopLossRequired,
      trailingStopLossType: formData.trailingStopLossType,
      tslMinProfitPercent: formData.tslMinProfitPercent,
      tslCallbackPercent: formData.tslCallbackPercent,
      trailingStopLossSRTimeFrame: Number(formData.trailingStopLossSRTimeFrame),
      breakEvenRequired: formData.breakEvenRequired,
      beMinProfitPercent: formData.beMinProfitPercent,
      breakEvenSRTimeFrame: Number(formData.breakEvenSRTimeFrame),
      partialTakeProfitRequired: formData.partialTakeProfitRequired,
      ptpMinProfitPercent: formData.ptpMinProfitPercent,
      ptpCloseSizePercent: formData.ptpCloseSizePercent,
      partialTakeProfitSRTimeFrame: Number(formData.partialTakeProfitSRTimeFrame),
      dcaRequired: formData.dcaRequired,
      dcaMinProfitPercent: formData.dcaMinProfitPercent,
      dcaAddSizePercent: formData.dcaAddSizePercent,
      dcaTimeFrame: Number(formData.dcaTimeFrame),
    };

    const entryResponse = await fetch(
      buildScopedApiUrl("/api/bot/ui/get/saveTradeEntryTracker", workspace),
      {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getApiHeaders() },
        body: JSON.stringify(entryPayload),
      }
    );
    if (!entryResponse.ok) {
      throw new Error(`trade entry tracker failed (HTTP ${entryResponse.status})`);
    }

    const entry = await entryResponse.json();
    const tradeSetupRequestId = entry?.id;
    if (!tradeSetupRequestId) {
      throw new Error("trade entry tracker response did not contain an id");
    }

    const takeProfitPayloads = buildTakeProfitTrackerPayloads(
      tradeSetupRequestId,
      "TradeSetupTEnEx",
      symbol
    );
    if (takeProfitPayloads.length > 0) {
      const takeProfitResponse = await fetch(
        buildScopedApiUrl("/api/bot/ui/get/saveBulkTakeProfitTrackers", workspace),
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...getApiHeaders() },
          body: JSON.stringify(takeProfitPayloads),
        }
      );
      if (!takeProfitResponse.ok) {
        throw new Error(`take profit trackers failed (HTTP ${takeProfitResponse.status})`);
      }
    }

    return {
      tradeSetupRequestId,
      tradeSide: entry?.tradeSide || entryPayload.tradeSide,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const bulkSymbols = Array.from(
      new Set(
        bulkSymbolsText
          .split(/[\s,;]+/)
          .map((symbol) => symbol.trim().toUpperCase())
          .filter(Boolean)
      )
    );

    if (bulkSymbols.length > 0) {
      if (formData.tradeEntryType !== "upcoming trade setup") {
        setLoading(false);
        setError("Bulk symbols are supported only for upcoming trade setups.");
        return;
      }
      if (
        formData.tradeEntryTrackerBase === "SR_LEVEL" ||
        formData.tpSrOpt === "YES" ||
        formData.slSrOpt === "YES"
      ) {
        setLoading(false);
        setError("Bulk symbols cannot be used with entry, take-profit, or stop-loss SR levels because those prices are symbol-specific.");
        return;
      }

      const allowedSymbols = new Set(symbolOptions.map((symbol) => String(symbol).toUpperCase()));
      const invalidSymbols = bulkSymbols.filter((symbol) => !allowedSymbols.has(symbol));
      if (invalidSymbols.length > 0) {
        setLoading(false);
        setError(`Unknown or inactive symbols: ${invalidSymbols.join(", ")}`);
        return;
      }

      const successes = [];
      const failures = [];
      for (const symbol of bulkSymbols) {
        try {
          const submittedSetup = await submitBulkUpcomingTradeSetupForSymbol(symbol);
          successes.push({ symbol, ...submittedSetup });
        } catch (submissionError) {
          failures.push({
            symbol,
            tradeSide: formData.side,
            message: submissionError.message || "Submission failed",
          });
        }
      }

      setSubmissionResult({
        message: `Bulk upcoming trade setup completed: ${successes.length}/${bulkSymbols.length} succeeded.`,
        tradeSetupRequestId: successes.map((item) => item.tradeSetupRequestId).join(", "),
        successes,
        failures,
      });
      setLoading(false);
      return;
    }

    // --------------------------------------------------
    // SR level validation logic (context-sensitive)
    // --------------------------------------------------
    let basePrice = Number(formData.limitPrice);

    // Ã¢Å“â€¦ For upcoming setups using SR_LEVEL base, validate from entry SR level
    if (
      formData.tradeEntryType === "upcoming trade setup" &&
      formData.tradeEntryTrackerBase === "SR_LEVEL" &&
      formData.tradeEntryTrackerValue
    ) {
      basePrice = Number(formData.tradeEntryTrackerValue);
    }

    const price = basePrice;
    const tp = Number(formData.tpSrLevel);
    const sl = Number(formData.slSrLevel);

    // Ã¢Å“â€¦ Proceed with directional validation
    if (formData.side && price) {
      if (formData.side === "open_long") {
        if (formData.tpSrOpt === "YES" && tp && tp <= price) {
          setLoading(false);
          setError("For LONG, Take Profit SR Level must be ABOVE base price.");
          return;
        }
        if (formData.slSrOpt === "YES" && sl && sl >= price) {
          setLoading(false);
          setError("For LONG, Stop Loss SR Level must be BELOW base price.");
          return;
        }
      } else if (formData.side === "open_short") {
        if (formData.tpSrOpt === "YES" && tp && tp >= price) {
          setLoading(false);
          setError("For SHORT, Take Profit SR Level must be BELOW base price.");
          return;
        }
        if (formData.slSrOpt === "YES" && sl && sl <= price) {
          setLoading(false);
          setError("For SHORT, Stop Loss SR Level must be ABOVE base price.");
          return;
        }
      }
    }

    try {
      let tradeSetupRequestId = null;

      // --------------------------------------------------
      // Case 1: orderType = upcoming trade setup
      // --------------------------------------------------
      if (formData.tradeEntryType === "upcoming trade setup") {
        // Save TradeEntryTracker first
        const tradeSetupExpiresAt = calculateTradeSetupExpiresAt();
        const entryPayload = {
          symbol: formData.symbol,
          exchange: workspace.exchange,
          marketType: workspace.marketType,
          timeFrame: Number(formData.tradeEntryTrackerTimeFrame || formData.tradingTimeFrame),
          tradeSide: formData.side,
          margin: formData.margin,
          leverage: formData.leverage,
          tradeType: formData.tradeType,
          orderType: formData.orderType,
          tradeEntryType: formData.tradeEntryType,
          tradeEntryTrackerStartedOn: new Date().toISOString(),
          tradeEntryTrackerType: composedTradeEntryTrackerType,
          tradeEntryTrackerValue: formData.tradeEntryTrackerValue,
          expectedCandleColorToOpen: "",
          attemptAllowed: Number(formData.attemptAllowed) || 1,
          attemptCompleted: 0,
          positionOpened: false,
          activeFlag: "Y",
          comments: formData.tradeEntryTrackerComments,
          journaling: formData.journaling,
          confluences: formData.confluences
                        ? formData.confluences.split(",").map((s) => s.trim())
                        : [],
          trackerSpecialIdentifier: formData.tradeEntryTrackerSpecialIdentifier || "TRADE_ENTRY",
          volumeBreakoutRequired: Boolean(formData.tradeEntryVolumeBreakoutRequired),
          expiryEnabled: Boolean(tradeSetupExpiresAt),
          expiresAt: tradeSetupExpiresAt,
          createdBy: "SYSTEM",
          trailingStopLossRequired: formData.trailingStopLossRequired,
          trailingStopLossType: formData.trailingStopLossType,
          tslMinProfitPercent: formData.tslMinProfitPercent,
          tslCallbackPercent: formData.tslCallbackPercent,
          trailingStopLossSRTimeFrame: Number(formData.trailingStopLossSRTimeFrame),

          breakEvenRequired: formData.breakEvenRequired,
          beMinProfitPercent: formData.beMinProfitPercent,
          breakEvenSRTimeFrame: Number(formData.breakEvenSRTimeFrame),

          partialTakeProfitRequired: formData.partialTakeProfitRequired,
          ptpMinProfitPercent: formData.ptpMinProfitPercent,
          ptpCloseSizePercent: formData.ptpCloseSizePercent,
          partialTakeProfitSRTimeFrame: Number(formData.partialTakeProfitSRTimeFrame),

          dcaRequired: formData.dcaRequired,
          dcaMinProfitPercent: formData.dcaMinProfitPercent,
          dcaAddSizePercent: formData.dcaAddSizePercent,
          dcaTimeFrame: Number(formData.dcaTimeFrame),

        };

        const res = await fetch(
          buildScopedApiUrl("/api/bot/ui/get/saveTradeEntryTracker", workspace),
          {
            method: "POST",
            headers: { "Content-Type": "application/json", ...getApiHeaders() },
            body: JSON.stringify(entryPayload),
          }
        );
        const entryResp = await res.json();
        tradeSetupRequestId = entryResp?.id;

        // --------------------------------------------------
        // Then save TakeProfitTrackers (bulk)
        // --------------------------------------------------
        const tpPayloads = [];

        const tpTrackerSourceValue =
            formData.tradeEntryType === "immediate" && formData.orderType === "market"
              ? "TradeSetupTEnEx With Immediate Market Order"
              : "TradeSetupTEnEx";
          // Ã¢Å“â€¦ Only create base tracker if takeProfitTrackerBase is actually selected
        tpPayloads.push(...buildTakeProfitTrackerPayloads(tradeSetupRequestId, tpTrackerSourceValue));

        // SR-levels
        if (formData.tpSrOpt === "YES" && formData.tpSrLevel) {
          tpPayloads.push({
            tradeSetupRequestId,
            symbol: formData.symbol,
            exchange: workspace.exchange,
            marketType: workspace.marketType,
            tradeSide: formData.side,
            timeFrame: String(formData.tpSrTimeFrame || getPrimaryTakeProfitTrackerTimeFrame()),
            takeProfitType: "SR_LEVEL",
            tpTrackerValue: formData.tpSrLevel,
            targetPercentage: targetPercentageTP,            
            activeFlag: "Y",
            trackerSpecialIdentifier: "TAKE_PROFIT_SR",
            tpTrackerSource: tpTrackerSourceValue,
            comments: getPrimaryTakeProfitTrackerComments(),
          });
        }
        if (formData.slSrOpt === "YES" && formData.slSrLevel) {
          tpPayloads.push({
            tradeSetupRequestId,
            symbol: formData.symbol,
            exchange: workspace.exchange,
            marketType: workspace.marketType,
            tradeSide: formData.side,
            timeFrame: String(formData.slSrTimeFrame || getPrimaryTakeProfitTrackerTimeFrame()),
            takeProfitType: "SR_LEVEL",
            tpTrackerValue: formData.slSrLevel,
            targetPercentage: targetPercentageSL,
            activeFlag: "Y",
            trackerSpecialIdentifier: "STOP_LOSS_SR",
            tpTrackerSource: tpTrackerSourceValue,
            comments: getPrimaryTakeProfitTrackerComments(),
          });
        }

        await fetch(
          buildScopedApiUrl("/api/bot/ui/get/saveBulkTakeProfitTrackers", workspace),
          {
            method: "POST",
            headers: { "Content-Type": "application/json", ...getApiHeaders() },
            body: JSON.stringify(tpPayloads),
          }
        );

        setSubmissionResult({
          message: "Upcoming trade setup saved successfully.",
          tradeSetupRequestId,
        });
      }

      // --------------------------------------------------
      // Case 2: orderType = market
      // --------------------------------------------------
      else if (formData.orderType === "market") {
        // normal order Ã¢â€ â€™ moneyMakerService then trackers
        const primaryTakeProfitTracker = formData.takeProfitTrackers?.[0] || {};
        const orderPayload = {
                    symbol: formData.symbol,
                    exchange: workspace.exchange,
                    marketType: workspace.marketType,
                    side: formData.side,
                    margin: formData.margin,
                    orderType: formData.orderType,
                    limitPrice: formData.limitPrice,
                    tradingTimeFrame: Number(formData.tradingTimeFrame),
                    activePositionCountLimitCheck: formData.activePositionCountLimitCheck,
                    takeProfitTracker: primaryTakeProfitTracker.tracker,
                    tpSlTrackerEmaCandleCross: primaryTakeProfitTracker.candleCross,
                  leverage: formData.leverage,
                  };

        const res = await fetch(
          buildTradeServiceUrl("/api/bot/ui/post/adhoc-order-service", workspace),
          {
            method: "POST",
            headers: { "Content-Type": "application/json", ...getApiHeaders() },
            body: JSON.stringify(orderPayload),
          }
        );
        const data = await res.json();
        const statusText = (data && data.status ? String(data.status) : "").toLowerCase();
        const orderOpened = statusText.includes("opened") || statusText.includes("success");

          // Ã¢Å“â€¦ Fetch current mark price before saving tracker (for immediate + market)
          let markPrice = formData.limitPrice;
          if (formData.tradeEntryType === "immediate" && formData.orderType === "market") {
            try {
              const resMark = await fetch(
                buildTradeServiceUrl("/api/bot/get/getMarkPriceBySymbol", workspace, {
                  symbol: formData.symbol,
                }),
                {
                  headers: getApiHeaders()
                });
              const dataMark = await resMark.json();
              markPrice = dataMark?.markPrice || formData.limitPrice;
            } catch (e) {
              console.warn("Ã¢Å¡Â Ã¯Â¸Â Failed to fetch mark price, fallback to limitPrice", e);
            }
          }

        if (orderOpened) {
          // save TradeEntryTracker
          const entryRes = await fetch(
            buildScopedApiUrl("/api/bot/ui/get/saveTradeEntryTracker", workspace),
            {
              method: "POST",
              headers: { "Content-Type": "application/json", ...getApiHeaders() },
              body: JSON.stringify({
                symbol: formData.symbol,
                exchange: workspace.exchange,
                marketType: workspace.marketType,
                tradeSide: formData.side,
                timeFrame: Number(formData.tradeEntryTrackerTimeFrame || formData.tradingTimeFrame),
                margin: formData.margin,
                orderType: formData.orderType,
                tradeEntryType: formData.tradeEntryType,
                tradeType: formData.tradeType,
                leverage: formData.leverage,                
                tradeEntryTrackerStartedOn: new Date().toISOString(),
                tradeEntryTrackerType:
                  formData.tradeEntryType === "immediate" && formData.orderType === "market"
                    ? "SR_LEVEL"
                    : composedTradeEntryTrackerType,
                tradeEntryTrackerValue:
                  formData.tradeEntryType === "immediate" && formData.orderType === "market"
                    ? markPrice
                    : formData.tradeEntryTrackerValue,
                attemptAllowed: Number(formData.attemptAllowed) || 1,
                attemptCompleted: 1,
                comments: formData.tradeEntryTrackerComments,
                journaling: formData.journaling,
                trackerSpecialIdentifier: formData.tradeEntryTrackerSpecialIdentifier || "TRADE_ENTRY",
                volumeBreakoutRequired: Boolean(formData.tradeEntryVolumeBreakoutRequired),
                expiryEnabled: false,
                expiresAt: null,
                activeFlag: "Y",
                createdBy: "SYSTEM",
                trailingStopLossRequired: formData.trailingStopLossRequired,
                trailingStopLossType: formData.trailingStopLossType,
                tslMinProfitPercent: formData.tslMinProfitPercent,
                tslCallbackPercent: formData.tslCallbackPercent,
                trailingStopLossSRTimeFrame: Number(formData.trailingStopLossSRTimeFrame),

                breakEvenRequired: formData.breakEvenRequired,
                beMinProfitPercent: formData.beMinProfitPercent,
                breakEvenSRTimeFrame: Number(formData.breakEvenSRTimeFrame),

                partialTakeProfitRequired: formData.partialTakeProfitRequired,
                ptpMinProfitPercent: formData.ptpMinProfitPercent,
                ptpCloseSizePercent: formData.ptpCloseSizePercent,
                partialTakeProfitSRTimeFrame: Number(formData.partialTakeProfitSRTimeFrame),

                dcaRequired: formData.dcaRequired,
                dcaMinProfitPercent: formData.dcaMinProfitPercent,
                dcaAddSizePercent: formData.dcaAddSizePercent,
                dcaTimeFrame: Number(formData.dcaTimeFrame),

              }),
            }
          );
          const entry = await entryRes.json();
          tradeSetupRequestId = entry?.id;

          const tpTrackerSourceValue =
            formData.tradeEntryType === "immediate" && formData.orderType === "market"
              ? "TradeSetupTEnEx With Immediate Market Order"
              : "TradeSetupTEnEx";

          // bulk trackers
          const tpBulk = [];
          tpBulk.push(...buildTakeProfitTrackerPayloads(tradeSetupRequestId, tpTrackerSourceValue));

          if (formData.tpSrOpt === "YES" && formData.tpSrLevel) {
            tpBulk.push({
              tradeSetupRequestId,
              symbol: formData.symbol,
              exchange: workspace.exchange,
              marketType: workspace.marketType,
              tradeSide: formData.side,
              timeFrame: String(formData.tpSrTimeFrame || getPrimaryTakeProfitTrackerTimeFrame()),
              takeProfitType: "SR_LEVEL",
              tpTrackerValue: formData.tpSrLevel,
              targetPercentage: targetPercentageTP,
              activeFlag: "Y",
              trackerSpecialIdentifier: "TAKE_PROFIT_SR",
              tpTrackerSource: tpTrackerSourceValue,
              comments: getPrimaryTakeProfitTrackerComments(),
            });
          }
          if (formData.slSrOpt === "YES" && formData.slSrLevel) {
            tpBulk.push({
              tradeSetupRequestId,
              symbol: formData.symbol,
              exchange: workspace.exchange,
              marketType: workspace.marketType,
              tradeSide: formData.side,
              timeFrame: String(formData.slSrTimeFrame || getPrimaryTakeProfitTrackerTimeFrame()),
              takeProfitType: "SR_LEVEL",
              tpTrackerValue: formData.slSrLevel,
              targetPercentage: targetPercentageSL,
              activeFlag: "Y",
              trackerSpecialIdentifier: "STOP_LOSS_SR",
              tpTrackerSource: tpTrackerSourceValue,
              comments: getPrimaryTakeProfitTrackerComments(),
            });
          }

          await fetch(
            buildScopedApiUrl("/api/bot/ui/get/saveBulkTakeProfitTrackers", workspace),
            {
              method: "POST",
              headers: { "Content-Type": "application/json", ...getApiHeaders() },
              body: JSON.stringify(tpBulk),
            }
          );
        }

        setSubmissionResult({
          message: "Market order executed & trackers saved.",
          tradeSetupRequestId,
        });
      }

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const renderTakeProfitTrackerSettings = (tracker, index) => {
    const composedType = composeTakeProfitTrackerType(tracker);
    const effectiveSpecialIdentifier = resolveTpTrackerSpecialIdentifier(tracker);
    const hideCrossMode =
      CROSS_HIDDEN_BASES.includes(tracker.trackerBase) ||
      CROSS_HIDDEN_EMA_OPTIONS.includes(tracker.tracker);

    const updateTrackerBase = (trackerBase) => {
      updateTakeProfitTracker(index, {
        trackerBase,
        tracker: "",
        candleCross: "FIRST_CANDLE_CROSS",
        trackerValue: "",
        specialIdentifier:
          trackerBase === "PROFIT_PERCENT"
            ? "TAKE_PROFIT"
            : trackerBase === "SL_PERCENT"
            ? "STOP_LOSS"
            : trackerBase === "EMA50_X_EMA200_CROSS"
            ? "EMA50_X_EMA200_EXIT"
            : "",
        closeSizePercentage: "100",
      });
    };

    const updatePercentValue = (rawValue, shouldBeNegative) => {
      let val = Number(rawValue);
      if (!isNaN(val)) {
        if (shouldBeNegative && val > 0) val = -Math.abs(val);
        if (!shouldBeNegative && val < 0) val = Math.abs(val);
        if (val === 0) val = "";
      }
      updateTakeProfitTracker(index, { trackerValue: val });
    };

    return (
      <div className="sr-section" key={index}>
        <label className="sr-label">Take Profit Tracker {index + 1}</label>

        {index === 2 && (
          <label className="volume-breakout-option">
            <input
              type="checkbox"
              checked={Boolean(tracker.enabled)}
              onChange={(e) => updateTakeProfitTracker(index, { enabled: e.target.checked })}
            />{" "}
            Enable Take Profit Tracker 3
          </label>
        )}

        <fieldset
          disabled={index === 2 && !tracker.enabled}
          style={{
            border: 0,
            margin: 0,
            padding: 0,
            minWidth: 0,
            opacity: index === 2 && !tracker.enabled ? 0.55 : 1,
          }}
        >

        <label>
          Tracker Base:
          <select
            value={tracker.trackerBase}
            onChange={(e) => updateTrackerBase(e.target.value)}
          >
            <option value="">Select</option>
            {TP_BASE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </label>

        {tracker.trackerBase === "EMA" && (
          <label>
            Take Profit Tracker:
            <select
              value={tracker.tracker}
              onChange={(e) => updateTakeProfitTracker(index, { tracker: e.target.value })}
            >
              <option value="">Select</option>
              {getFilteredEmaOptions(formData.side).map((ema) => (
                <option key={ema} value={ema}>{ema}</option>
              ))}
            </select>
          </label>
        )}

        {tracker.trackerBase === "TREND_SHIFT_SIGNAL" && (
          <label>
            Trend Shift Signal:
            <select
              value={tracker.tracker}
              onChange={(e) => updateTakeProfitTracker(index, { tracker: e.target.value })}
            >
              <option value="">Select</option>
              {TREND_SHIFT_SIGNAL_OPTIONS.map((signal) => (
                <option key={signal} value={signal}>{signal}</option>
              ))}
            </select>
          </label>
        )}
        {tracker.trackerBase === "VWAP" && (
          <label>
            VWAP Mode:
            <select
              value={tracker.tracker}
              onChange={(e) => updateTakeProfitTracker(index, { tracker: e.target.value })}
            >
              <option value="">Select</option>
              <option value="DAILY">DAILY</option>
              <option value="WEEKLY">WEEKLY</option>
            </select>
          </label>
        )}

        {tracker.trackerBase === "SWING_HIGH_LOW" && (
          <label>
            Swing Mode:
            <select
              value={tracker.tracker}
              onChange={(e) => updateTakeProfitTracker(index, { tracker: e.target.value })}
            >
              <option value="">Select</option>
              <option value="SWING_HIGH">SWING_HIGH</option>
              <option value="SWING_LOW">SWING_LOW</option>
              <option value="RECENT_SWING_HIGH">RECENT_SWING_HIGH</option>
              <option value="RECENT_SWING_LOW">RECENT_SWING_LOW</option>
            </select>
          </label>
        )}

        <label>
          Take Profit Tracker Time Frame:
          <select
            value={tracker.timeFrame || formData.tradingTimeFrame}
            onChange={(e) => updateTakeProfitTracker(index, { timeFrame: e.target.value })}
          >
            {TIMEFRAME_OPTIONS.map(tf => (
              <option key={tf} value={tf}>
                {tf} min
              </option>
            ))}
          </select>
        </label>

        {!hideCrossMode && (
          <label>
            Candle Cross Mode:
            <select
              value={tracker.candleCross}
              onChange={(e) => updateTakeProfitTracker(index, { candleCross: e.target.value })}
            >
              <option value="">Select</option>
              <option value="FIRST_CANDLE_CROSS">FIRST_CANDLE_CROSS</option>
              <option value="CONFIRMED_CANDLE_CROSS">CONFIRMED_CANDLE_CROSS</option>
            </select>
          </label>
        )}

        {composedType && (
          <div style={{ fontSize: 12, color: "#666", marginTop: "6px" }}>
            Will save as <code>{composedType}</code>
          </div>
        )}

        {tracker.trackerBase !== "EMA50_X_EMA200_CROSS" && (
        <label className="volume-breakout-option">
          <input
            type="checkbox"
            checked={Boolean(tracker.volumeBreakoutRequired)}
            onChange={(e) => updateTakeProfitTracker(index, { volumeBreakoutRequired: e.target.checked })}
          />{" "}
          Require Volume Breakout
        </label>
        )}
        {tracker.trackerBase === "PROFIT_PERCENT" && (
          <label>
            Profit % Threshold:
            <input
              type="number"
              value={tracker.trackerValue}
              onChange={(e) => updatePercentValue(e.target.value, false)}
              placeholder="e.g. 5 for 5%"
            />
          </label>
        )}

        {tracker.trackerBase === "SL_PERCENT" && (
          <label>
            Stop Loss % Threshold:
            <input
              type="number"
              value={tracker.trackerValue}
              onChange={(e) => updatePercentValue(e.target.value, true)}
              placeholder="e.g. -5 for -5%"
            />
          </label>
        )}

        <label>
          Tracker Special Identifier (TP):
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <select
              value={effectiveSpecialIdentifier}
              onChange={(e) => updateTakeProfitTracker(index, { specialIdentifier: e.target.value })}
              style={{ flex: 1 }}
              disabled={
                tracker.trackerBase === "SL_PERCENT" ||
                tracker.trackerBase === "PROFIT_PERCENT" ||
                tracker.trackerBase === "EMA50_X_EMA200_CROSS"
              }
            >
              <option value="CUSTOM">Custom</option>
              <option value="DEMAND ZONE HIGH">DEMAND ZONE HIGH</option>
              <option value="SUPPLY ZONE LOW">SUPPLY ZONE LOW</option>
              <option value="KEY SR LEVEL">KEY SR LEVEL</option>
              <option value="MS RETEST & REVERSAL">MS RETEST BOUNCE</option>
              <option value="BULLISH MSB">BULLISH MSB</option>
              <option value="BEARISH MSB">BEARISH MSB</option>
              <option value="TAKE_PROFIT">TAKE_PROFIT</option>
              <option value="STOP_LOSS">STOP_LOSS</option>
              <option value="EMA50_X_EMA200_EXIT">EMA50_X_EMA200_EXIT</option>
            </select>

            <input
              type="text"
              value={tracker.specialIdentifier === "CUSTOM" ? "" : tracker.specialIdentifier}
              onChange={(e) => updateTakeProfitTracker(index, { specialIdentifier: e.target.value })}
              placeholder="Or type custom identifier"
              style={{ flex: 1 }}
              disabled={
                tracker.trackerBase === "SL_PERCENT" ||
                tracker.trackerBase === "PROFIT_PERCENT" ||
                tracker.trackerBase === "EMA50_X_EMA200_CROSS"
              }
            />
          </div>

          {tracker.trackerBase === "SL_PERCENT" && (
            <div
              style={{
                fontSize: "12px",
                color: "#d35400",
                marginTop: "4px",
                fontStyle: "italic",
              }}
            >
              STOP_LOSS is automatically selected for SL_PERCENT mode.
            </div>
          )}

        {tracker.trackerBase === "PROFIT_PERCENT" && (
            <div
              style={{
                fontSize: "12px",
                color: "#16a085",
                marginTop: "4px",
                fontStyle: "italic",
              }}
            >
              TAKE_PROFIT is automatically selected for PROFIT_PERCENT mode.
            </div>
          )}
        </label>

        <label>
          Take Profit Tracker Comments:
          <textarea
            rows="2"
            value={tracker.comments}
            onChange={(e) => updateTakeProfitTracker(index, { comments: e.target.value })}
            placeholder="Notes specific to Take Profit Tracker"
          />
        </label>
        </fieldset>
      </div>
    );
  };

  // ------------------------------------------------------
  // Render
  // ------------------------------------------------------
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      contentLabel="Trade Setup TEnEx"
      className="ReactModal__Content tenex-modal-content"
      overlayClassName="ReactModal__Overlay tenex-modal-overlay"
    >
      <button className="modal-close-button" onClick={closeModal} type="button" aria-label="Close Trade Setup TEnEx modal">
        <span className="modal-close-button__icon" aria-hidden="true">&times;</span>
        <span>Close</span>
      </button>
      <h2 className="page-title">Trade Setup TEnEx (Enhanced)</h2>
      <p style={{ marginTop: 0, color: "#666" }}>
        Workspace: <strong>{workspace.exchange}</strong> / <strong>{workspace.marketType}</strong>
      </p>

      {loading && <p className="loading-message">Submitting...</p>}
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      {submissionResult && (
        <div style={{ color: submissionResult.failures?.length ? "#b36b00" : "green" }}>
          <p>
            {submissionResult.message}
            {!submissionResult.successes && submissionResult.tradeSetupRequestId
              ? ` ID: ${submissionResult.tradeSetupRequestId}`
              : ""}
          </p>
          {submissionResult.successes && (
            <div className="bulk-submission-results">
              <table>
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Symbol</th>
                    <th>Side</th>
                    <th>Setup ID</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {submissionResult.successes.map((item, index) => (
                    <tr key={`success-${item.symbol}`}>
                      <td>{index + 1}</td>
                      <td>{item.symbol}</td>
                      <td>{item.tradeSide || formData.side || "-"}</td>
                      <td>{item.tradeSetupRequestId}</td>
                      <td className="bulk-result-success">SUCCESS</td>
                    </tr>
                  ))}
                  {submissionResult.failures?.map((item, index) => (
                    <tr key={`failure-${item.symbol}`}>
                      <td>{submissionResult.successes.length + index + 1}</td>
                      <td>{item.symbol}</td>
                      <td>{item.tradeSide || formData.side || "-"}</td>
                      <td>-</td>
                      <td className="bulk-result-failure">FAILED: {item.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {!loading && !submissionResult && (
        <form className="modal-form" onSubmit={handleSubmit}>
          {/* Basic order fields */}
          <label>
            Symbol:
            <select
              name="symbol"
              value={formData.symbol}
              onChange={handleInputChange}
            >
              {symbolOptions.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </label>

          {formData.tradeEntryType === "upcoming trade setup" && (
            <label className="full">
              Bulk Symbols (optional):
              <textarea
                rows="3"
                value={bulkSymbolsText}
                onChange={(e) => setBulkSymbolsText(e.target.value)}
                placeholder="Paste comma-, space-, or newline-separated symbols, e.g. ADAUSDT, SOLUSDT, LINKUSDT"
              />
              <span style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
                When provided, the same upcoming setup is submitted sequentially for each symbol. SR-level selections are not supported in bulk mode.
              </span>
            </label>
          )}

            {/* Ã¢Å“â€¦ Trade Entry Type (immediate / upcoming trade setup) */}
          <label>
            Trade Entry Type:
            <select
              name="tradeEntryType"
              value={formData.tradeEntryType}
              onChange={handleInputChange}
            >
              <option value="">Select</option>
              <option value="immediate">immediate</option>
              <option value="upcoming trade setup">upcoming trade setup</option>
            </select>
          </label>

          {/* Ã¢Å“â€¦ Order Type (market / limit / trigger) */}
          <label>
            Order Type:
            <select
              name="orderType"
              value={formData.orderType}
              onChange={handleInputChange}
            >
              <option value="" disabled>Select</option>
              <option value="market">market</option>
              <option value="limit" disabled>limit (not supported)</option>
              <option value="trigger" disabled>trigger (not supported)</option>
            </select>
          </label>

          <label>
            Side:
            <select
              name="side"
              value={formData.side}
              onChange={handleInputChange}
            >
              <option value="">Select Side</option>
              <option value="open_long">open_long</option>
              <option value="open_short">open_short</option>
            </select>
          </label>

          <label>
            Trading Time Frame:
            <select
              name="tradingTimeFrame"
              value={formData.tradingTimeFrame}
              onChange={handleInputChange}
            >
              <option value="">Select Time Frame</option>
              {TIMEFRAME_OPTIONS.map((tf) => (
                <option key={tf} value={tf}>
                  {tf} min
                </option>
              ))}
            </select>
          </label>

          <label>
            Limit Price:
            <input
              name="limitPrice"
              value={formData.limitPrice}
              onChange={handleInputChange}
            />
          </label>

          <label>
            Margin:
            <input
              name="margin"
              value={formData.margin}
              onChange={handleInputChange}
            />
          </label>

          <label>
            Leverage:
            <select
              name="leverage"
              value={formData.leverage}
              onChange={handleInputChange}
            >
              <option value="">Select</option>
              {[1, 2, 3, 5, 10, 15, 20].map((lev) => (
                <option key={lev} value={lev}>
                  {lev}x
                </option>
              ))}
            </select>
          </label>

          <label>
            Trade Type:
            <select
              name="tradeType"
              value="FRESH"
              onChange={handleInputChange}
              disabled
            >
              <option value="FRESH">FRESH</option>
            </select>
          </label>

 {/* Trade Entry Tracker section */}

          {/* Ã¢Å“â€¦ Trade Entry Tracker Section */}
          <div className="sr-section">
            <label className="sr-label">Trade Entry Tracker Settings</label>

            {formData.tradeEntryType  !== "upcoming trade setup" ? (
              <div style={{ fontSize: 12, color: "#777", marginBottom: "8px" }}>
                (Available only for "upcoming trade setup" orders)
              </div>
            ) : (
              <>

                <label>
                  Trade Entry Tracker Base:
                  <select
                    name="tradeEntryTrackerBase"
                    value={formData.tradeEntryTrackerBase || ""}
                    onChange={handleInputChange}
                    disabled={formData.tradeEntryType  !== "upcoming trade setup"}
                  >
                    <option value="">Select Base</option>
                    {TE_BASE_OPTIONS.map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </select>
                </label>

                {/* Ã¢Å“â€¦ SR Level visible when base = SR_LEVEL */}

                {formData.tradeEntryTrackerBase === "SR_LEVEL" && (
                <label>
                   SR Level:
                  <input
                    name="tradeEntryTrackerValue"
                    value={formData.tradeEntryTrackerValue}
                    onChange={handleInputChange}
                    disabled={formData.tradeEntryType  !== "upcoming trade setup"}
                  />
                </label>
                )}

                {formData.tradeEntryTrackerBase === "EMA" && (
                  <label>
                    Trade Entry Tracker:
                    <select
                      name="tradeEntryTracker"
                      value={formData.tradeEntryTracker}
                      onChange={handleInputChange}
                    >
                      <option value="">Select</option>
                      {getFilteredEmaOptions(formData.side).map((ema) => (
                        <option key={ema} value={ema}>{ema}</option>
                      ))}
                    </select>

                  </label>
                )}

                {formData.tradeEntryTrackerBase === "TREND_SHIFT_SIGNAL" && (
                  <label>
                    Trend Shift Signal:
                    <select
                      name="tradeEntryTracker"
                      value={formData.tradeEntryTracker}
                      onChange={handleInputChange}
                      disabled={formData.tradeEntryType  !== "upcoming trade setup"}
                    >
                      <option value="">Select</option>
                      {TREND_SHIFT_SIGNAL_OPTIONS.map((signal) => (
                        <option key={signal} value={signal}>{signal}</option>
                      ))}
                    </select>
                  </label>
                )}
                {formData.tradeEntryTrackerBase === MULTI_TIMEFRAME_CONFLUENCE && (
                  <div className="confluence-rule-preview">
                    <label>
                      Confluence Strategy:
                      <select
                        name="tradeEntryTrackerValue"
                        value={formData.tradeEntryTrackerValue || ""}
                        onChange={handleInputChange}
                        disabled={formData.tradeEntryType !== "upcoming trade setup"}
                      >
                        <option value="">Select Confluence</option>
                        {confluenceStrategies.map((item) => (
                          <option
                            key={item?.strategy?.strategyCode}
                            value={item?.strategy?.strategyCode}
                          >
                            {item?.strategy?.strategyName || item?.strategy?.strategyCode}
                          </option>
                        ))}
                      </select>
                    </label>

                    {selectedConfluenceStrategy?.rules?.length > 0 && (
                      <div className="confluence-rule-list">
                        <div className="confluence-rule-title">Rules</div>
                        {selectedConfluenceStrategy.rules.map((rule) => (
                          <div className="confluence-rule-row" key={rule.id || rule.ruleCode}>
                            <strong>{rule.timeFrame} min</strong>
                            <span>{rule.conditionType}</span>
                            <code>{rule.signalKey}</code>
                            {rule.ruleDescription && <small>{rule.ruleDescription}</small>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {formData.tradeEntryTrackerBase === "VWAP" && (
                  <label>
                    VWAP Mode:
                    <select
                      name="tradeEntryTracker"
                      value={formData.tradeEntryTracker}
                      onChange={handleInputChange}
                      disabled={formData.tradeEntryType  !== "upcoming trade setup"}
                    >
                      <option value="DAILY">DAILY</option>
                      <option value="WEEKLY">WEEKLY</option>
                    </select>
                  </label>
                )}

                {formData.tradeEntryTrackerBase === "SWING_HIGH_LOW" && (
                  <label>
                    Swing Mode:
                    <select
                      name="tradeEntryTracker"
                      value={formData.tradeEntryTracker}
                      onChange={handleInputChange}
                      disabled={formData.tradeEntryType  !== "upcoming trade setup"}
                    >
                      <option value="SWING_HIGH">SWING_HIGH</option>
                      <option value="SWING_LOW">SWING_LOW</option>
                      <option value="RECENT_SWING_HIGH">RECENT_SWING_HIGH</option>
                      <option value="RECENT_SWING_LOW">RECENT_SWING_LOW</option>
                    </select>
                  </label>
                )}

                  {!CROSS_HIDDEN_BASES.includes(formData.tradeEntryTrackerBase) &&
                    !CROSS_HIDDEN_EMA_OPTIONS.includes(formData.tradeEntryTracker) && (
                      <label>
                      Trade Entry Tracker Cross Mode:
                      <select
                        name="tradeEntryTrackerCross"
                        value={formData.tradeEntryTrackerCross || "FIRST_CANDLE_CROSS"}
                        onChange={handleInputChange}
                        disabled={formData.tradeEntryType  !== "upcoming trade setup"}
                      >
                        <option value="FIRST_CANDLE_CROSS">FIRST_CANDLE_CROSS</option>
                        <option value="CONFIRMED_CANDLE_CROSS">CONFIRMED_CANDLE_CROSS</option>
                      </select>
                    </label>
                  )}

                {composedTradeEntryTrackerType && (
                  <div style={{ fontSize: 12, color: "#666" }}>
                    Will save as <code>{composedTradeEntryTrackerType}</code>
                  </div>
                )}

                <label className="volume-breakout-option">
                  <input
                    type="checkbox"
                    name="tradeEntryVolumeBreakoutRequired"
                    checked={Boolean(formData.tradeEntryVolumeBreakoutRequired)}
                    onChange={handleInputChange}
                    disabled={formData.tradeEntryType !== "upcoming trade setup"}
                  />{" "}
                  Require Volume Breakout
                </label>
                <label>
                  Attempt Allowed:
                  <select
                    name="attemptAllowed"
                    value={formData.attemptAllowed}
                    onChange={handleInputChange}
                  >
                    {[1, 2, 3, 4, 5, 10, 25, 50, 100, 200, 500].map((num) => (
                      <option key={num} value={num}>
                        {num}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  Trade Setup Expiry:
                  <select
                    name="tradeSetupExpiryMode"
                    value={formData.tradeSetupExpiryMode}
                    onChange={handleInputChange}
                    disabled={formData.tradeEntryType !== "upcoming trade setup"}
                  >
                    {TRADE_SETUP_EXPIRY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                {formData.tradeSetupExpiryMode === "CUSTOM" && (
                  <div className="expiry-custom-row">
                    <label>
                      Expiry Value:
                      <input
                        type="number"
                        min="1"
                        name="customTradeSetupExpiryValue"
                        value={formData.customTradeSetupExpiryValue}
                        onChange={handleInputChange}
                        disabled={formData.tradeEntryType !== "upcoming trade setup"}
                        placeholder="e.g. 6"
                      />
                    </label>
                    <label>
                      Expiry Unit:
                      <select
                        name="customTradeSetupExpiryUnit"
                        value={formData.customTradeSetupExpiryUnit}
                        onChange={handleInputChange}
                        disabled={formData.tradeEntryType !== "upcoming trade setup"}
                      >
                        <option value="HOURS">Hours</option>
                        <option value="DAYS">Days</option>
                        <option value="WEEKS">Weeks</option>
                      </select>
                    </label>
                  </div>
                )}

                <label>
                  Trade Entry Tracker Time Frame:
                  <span style={{ fontSize: "12px", color: "#888", marginLeft: "4px" }}>
                    (mirrors general timeframe)
                  </span>
                  <select
                    name="tradeEntryTrackerTimeFrame"
                    value={formData.tradeEntryTrackerTimeFrame || formData.tradingTimeFrame}
                    onChange={handleInputChange}
                    disabled
                  >
                    {TIMEFRAME_OPTIONS.map(tf => (
                      <option key={tf} value={tf}>
                        {tf} min
                      </option>
                    ))}
                  </select>

                </label>

                <label>
                  Tracker Special Identifier (Entry):
                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    {/* Dropdown for common identifiers */}
                    <select
                      name="tradeEntryTrackerSpecialIdentifier"
                      value={formData.tradeEntryTrackerSpecialIdentifier}
                      onChange={handleInputChange}
                      disabled={formData.tradeEntryType !== "upcoming trade setup"}
                      style={{ flex: 1 }}
                    >
                      <option value="CUSTOM">Custom</option>
                      <option value="DEMAND ZONE HIGH">DEMAND ZONE HIGH</option>
                      <option value="SUPPLY ZONE LOW">SUPPLY ZONE LOW</option>
                      <option value="KEY SR LEVEL">KEY SR LEVEL</option>
                      <option value="MS RETEST & REVERSAL">MS RETEST BOUNCE</option>
                      <option value="BULLISH MSB">BULLISH MSB</option>
                      <option value="BEARISH MSB">BEARISH MSB</option>
                    </select>

                    {/* Free-text input to override or create custom identifier */}
                    <input
                      type="text"
                      name="tradeEntryTrackerSpecialIdentifier"
                      value={
                        formData.tradeEntryTrackerSpecialIdentifier === "CUSTOM"
                          ? ""
                          : formData.tradeEntryTrackerSpecialIdentifier
                      }
                      onChange={handleInputChange}
                      placeholder="Or type custom identifier"
                      disabled={formData.tradeEntryType !== "upcoming trade setup"}
                      style={{ flex: 1 }}
                    />
                  </div>
                </label>

               <label>
                 Trade Entry Tracker Comments:
                 <textarea
                   name="tradeEntryTrackerComments"
                   rows="2"
                   value={formData.tradeEntryTrackerComments}
                   onChange={handleInputChange}
                   placeholder="Notes specific to Trade Entry Tracker"
                   disabled={formData.tradeEntryType !== 'upcoming trade setup'}
                 />
               </label>

              </>
            )}
          </div>

          {/* SR editable levels */}

          {/* Ã¢Å“â€¦ Take Profit SR section */}
          <div className="sr-section">
            <label className="sr-label">Include Take Profit SR Level?</label>
            <div className="radio-group">
              <label>
                <input
                  type="radio"
                  name="tpSrOpt"
                  value="YES"
                  checked={formData.tpSrOpt === "YES"}
                  onChange={handleInputChange}
                />{" "}
                Yes
              </label>
              <label>
                <input
                  type="radio"
                  name="tpSrOpt"
                  value="NO"
                  checked={formData.tpSrOpt === "NO"}
                  onChange={handleInputChange}
                />{" "}
                No
              </label>
            </div>

            {formData.tpSrOpt === "YES" && (
              <div className="sr-input">
                <label>
                  Take Profit SR Level:
                 {targetPercentageTP && (
                      <span
                        className={`pct-badge ${Number(targetPercentageTP) >= 0 ? "pct-pos" : "pct-neg"}`}
                      >
                        ({Number(targetPercentageTP) >= 0 ? "+" : ""}
                        {targetPercentageTP}%)
                      </span>
                    )}

                  <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                    <input
                      name="tpSrLevel"
                      value={formData.tpSrLevel}
                      onChange={handleInputChange}
                      disabled={!formData.side}
                      style={{ flex: 2 }}
                    />
                    <input
                      type="number"
                      value={tpCustomPct || targetPercentageTP || ""}
                      onChange={(e) => {
                        const mag = sanitizePct(e.target.value);
                        setTpCustomPct(mag);
                        const newLevel = recalcLevelFromPct(formData, mag, "TP");
                        setFormData((p) => ({ ...p, tpSrLevel: newLevel }));
                      }}
                      style={{ width: "60px" }}
                    />
                    <span style={{ fontSize: "12px" }}>%</span>
                  </div>
                </label>
              </div>
            )}

          <label>
            Take Profit SR Time Frame:
            <select
              name="tpSrTimeFrame"
              value={formData.tpSrTimeFrame}
              onChange={handleInputChange}
            >
              {TIMEFRAME_OPTIONS.map(tf => (
                <option key={tf} value={tf}>{tf} min</option>
              ))}
            </select>
          </label>

          </div>

          {/* Ã¢Å“â€¦ Stop Loss SR section */}
          <div className="sr-section">
            <label className="sr-label">Include Stop Loss SR Level?</label>
            <div className="radio-group">
              <label>
                <input
                  type="radio"
                  name="slSrOpt"
                  value="YES"
                  checked={formData.slSrOpt === "YES"}
                  onChange={handleInputChange}
                />{" "}
                Yes
              </label>
              <label>
                <input
                  type="radio"
                  name="slSrOpt"
                  value="NO"
                  checked={formData.slSrOpt === "NO"}
                  onChange={handleInputChange}
                />{" "}
                No
              </label>
            </div>

            {formData.slSrOpt === "YES" && (
              <div className="sr-input">
                <label>
                  Stop Loss SR Level:
                  {targetPercentageSL && (
                      <span
                        className={`pct-badge ${Number(targetPercentageSL) >= 0 ? "pct-pos" : "pct-neg"}`}
                      >
                        ({Number(targetPercentageSL) >= 0 ? "+" : ""}
                        {targetPercentageSL}%)
                      </span>
                    )}

                  <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                    <input
                      name="slSrLevel"
                      value={formData.slSrLevel}
                      onChange={handleInputChange}
                      disabled={!formData.side}
                      style={{ flex: 2 }}
                    />
                    <input
                      type="number"
                      value={slCustomPct || targetPercentageSL || ""}
                      onChange={(e) => {
                        const mag = sanitizePct(e.target.value);   // <- strip "-" safely
                        setSlCustomPct(mag);
                        const newLevel = recalcLevelFromPct(formData, mag, "SL");
                        setFormData((p) => ({ ...p, slSrLevel: newLevel }));
                      }}
                      style={{ width: "60px" }}
                    />

                    <span style={{ fontSize: "12px" }}>%</span>
                  </div>
                </label>

                <label>
                  Stop Loss SR Time Frame:
                  <select
                    name="slSrTimeFrame"
                    value={formData.slSrTimeFrame}
                    onChange={handleInputChange}
                  >
                    {TIMEFRAME_OPTIONS.map(tf => (
                      <option key={tf} value={tf}>{tf} min</option>
                    ))}
                  </select>
                </label>

              </div>
            )}

          </div>

      {/* Ã¢Å“â€¦ Create Take Profit Tracker option */}
      <div className="sr-section">
        <label className="sr-label">
          Create Take Profit Tracker after placing order?
        </label>
        <div className="radio-group">
          <label>
            <input
              type="radio"
              name="addTpTrackerOpt"
              value="YES"
              checked={formData.addTpTrackerOpt === "YES"}
              onChange={handleInputChange}
            />{" "}
            Yes
          </label>
          <label>
            <input
              type="radio"
              name="addTpTrackerOpt"
              value="NO"
              checked={formData.addTpTrackerOpt === "NO"}
              onChange={handleInputChange}
            />{" "}
            No
          </label>
        </div>
      </div>

      {formData.addTpTrackerOpt === "YES" && (
        <>
          {formData.takeProfitTrackers.map((tracker, index) =>
            renderTakeProfitTrackerSettings(tracker, index)
          )}
        </>
      )}

      {/* ======================== AUTOMATION SETTINGS ============================= */}
      <div className="sr-section">
        <label className="sr-label">Automation Settings</label>

        {/* ========== TRAILING STOP LOSS ========== */}
      <label>Trailing Stop Loss?</label>
      <select
        name="trailingStopLossRequired"
        value={formData.trailingStopLossRequired}
        onChange={(e) =>
          setFormData({
            ...formData,
            trailingStopLossRequired: e.target.value === "true"
          })
        }
      >
        <option value="false">NO</option>
        <option value="true">YES</option>
      </select>

      {formData.trailingStopLossRequired && (
        <>
          {/* NEW: TSL Type Selector */}
          <label>TSL Type:</label>
          <select
            name="trailingStopLossType"
            value={formData.trailingStopLossType}
            onChange={handleInputChange}
          >
            <option value="PREV_LES_EDGE_CROSS_SL">PREV_LES_EDGE_CROSS_SL</option>
            <option value="PREV_OPP_CANDLE_CLOSE_SL">PREV_OPP_CANDLE_CLOSE_SL</option>
            <option value="PREV_CANDLE_TREND_REVERSAL_SL">
              PREV_CANDLE_TREND_REVERSAL_SL
            </option>
            <option value="PERCENT_CALLBACK_TSL">PERCENT_CALLBACK_TSL</option>
          </select>

          <label>TSL Min Profit %:</label>
          <input
            type="number"
            name="tslMinProfitPercent"
            value={formData.tslMinProfitPercent}
            onChange={handleInputChange}
          />

          {formData.trailingStopLossType === "PERCENT_CALLBACK_TSL" && (
            <>
              <label>TSL Callback %:</label>
              <input
                type="number"
                step="0.1"
                min="0"
                name="tslCallbackPercent"
                value={formData.tslCallbackPercent}
                onChange={handleInputChange}
              />
            </>
          )}

          <label>TSL Timeframe:</label>
          <select
            name="trailingStopLossSRTimeFrame"
            value={formData.trailingStopLossSRTimeFrame}
            onChange={handleInputChange}
          >
            {TIMEFRAME_OPTIONS.map((tf) => (
              <option key={tf} value={tf}>{tf}</option>
            ))}
          </select>
        </>
      )}

  {/* ========== BREAK EVEN ========== */}
  <label>Break Even?</label>
  <select
    name="breakEvenRequired"
    value={formData.breakEvenRequired}
    onChange={(e) =>
      setFormData({ ...formData, breakEvenRequired: e.target.value === "true" })
    }
  >
    <option value="false">NO</option>
    <option value="true">YES</option>
  </select>

  {formData.breakEvenRequired && (
    <>
      <label>Break Even Min Profit %:</label>
      <input
        type="number"
        name="beMinProfitPercent"
        value={formData.beMinProfitPercent}
        onChange={handleInputChange}
      />

      <label>Break Even SR Timeframe:</label>
      <select
        name="breakEvenSRTimeFrame"
        value={formData.breakEvenSRTimeFrame}
        onChange={handleInputChange}
      >
        {TIMEFRAME_OPTIONS.map((tf) => (
          <option key={tf} value={tf}>{tf}</option>
        ))}
      </select>
    </>
  )}

  {/* ========== PARTIAL TAKE PROFIT ========== */}
  <label>Partial Take Profit?</label>
  <select
    name="partialTakeProfitRequired"
    value={formData.partialTakeProfitRequired}
    onChange={(e) =>
      setFormData({ ...formData, partialTakeProfitRequired: e.target.value === "true" })
    }
  >
    <option value="false">NO</option>
    <option value="true">YES</option>
  </select>

  {formData.partialTakeProfitRequired && (
    <>
      <label>PTP Min Profit %:</label>
      <input
        type="number"
        name="ptpMinProfitPercent"
        value={formData.ptpMinProfitPercent}
        onChange={handleInputChange}
      />

      <label>PTP Close Size %:</label>
      <input
        type="number"
        name="ptpCloseSizePercent"
        value={formData.ptpCloseSizePercent}
        onChange={handleInputChange}
      />

      <label>PTP Tracker Timeframe:</label>
      <select
        name="partialTakeProfitSRTimeFrame"
        value={formData.partialTakeProfitSRTimeFrame}
        onChange={handleInputChange}
      >
        {TIMEFRAME_OPTIONS.map((tf) => (
          <option key={tf} value={tf}>{tf}</option>
        ))}
      </select>
    </>
  )}

  {/* ========== DCA ========== */}
  <label>DCA Required?</label>
  <select
    name="dcaRequired"
    value={formData.dcaRequired}
    onChange={(e) =>
      setFormData({ ...formData, dcaRequired: e.target.value === "true" })
    }
  >
    <option value="false">NO</option>
    <option value="true">YES</option>
  </select>

  {formData.dcaRequired && (
    <>
      <label>DCA Min Profit %:</label>
      <input
        type="number"
        name="dcaMinProfitPercent"
        value={formData.dcaMinProfitPercent}
        onChange={handleInputChange}
      />

      <label>DCA Add Size %:</label>
      <input
        type="number"
        name="dcaAddSizePercent"
        value={formData.dcaAddSizePercent}
        onChange={handleInputChange}
      />

      <label>DCA Timeframe:</label>
      <select
        name="dcaTimeFrame"
        value={formData.dcaTimeFrame}
        onChange={handleInputChange}
      >
        {TIMEFRAME_OPTIONS.map((tf) => (
          <option key={tf} value={tf}>{tf}</option>
        ))}
      </select>
    </>
  )}
</div>
{/* ======================== END AUTOMATION ============================= */}

          <label>
            Comments:
            <textarea
              name="comments"
              rows="2"
              value={formData.comments}
              onChange={handleInputChange}
            />
          </label>

          <label>
            Journaling:
            <textarea
              name="journaling"
              rows="2"
              value={formData.journaling}
              onChange={handleInputChange}
            />
          </label>

          <label>
            Confluences (comma separated):
            <input
              type="text"
              name="confluences"
              value={formData.confluences || ""}
              onChange={handleInputChange}
              placeholder="e.g. EMA36, VWAP, SR_LEVEL"
            />
          </label>

          <label>
            Active Position Count Limit Check:
            <select
              name="activePositionCountLimitCheck"
              value={formData.activePositionCountLimitCheck}
              onChange={handleInputChange}
            >
              <option value="REQUIRED">REQUIRED</option>
              <option value="SKIP">SKIP</option>
            </select>
          </label>

          <div className="modal-button-container">
            <button type="submit">Submit</button>
            <button type="button" onClick={closeModal}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

export default TradeSetupTEnEx;
